import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatTimestamp, formatHandle } from '../utils/formatters';
import LinkPreview from './LinkPreview';
import ReplySection from './ReplySection';
import UserProfile from './UserProfile';
import CensoredContent from './CensoredContent';
import { toast } from 'react-hot-toast';
import { 
  MessageCircle, 
  Repeat2, 
  Heart, 
  Bookmark, 
  Share2
} from 'lucide-react';

const Post = ({ 
  post, 
  onLike,
  onRepost,
  onLoginRequired, 
  isLoggedIn, 
  isLoading,
  bluesky,
  bookmarkService,
  onBookmarkRemove,
  isSavedView
}) => {
  // Validate post data before any hooks
  const isValidPost = post?.post?.uri && post?.post?.author;
  
  // Initialize bookmark state - safe even if post is invalid
  const [isBookmarked, setIsBookmarked] = useState(() => 
    isValidPost ? bookmarkService.isBookmarked(post.post.cid) : false
  );
  const [showProfile, setShowProfile] = useState(false);
  const [showReplies, setShowReplies] = useState(false);

  // Memoize postData to avoid recalculation
  const postData = useMemo(() => isValidPost ? post.post : null, [post, isValidPost]);
  
  // Early return after all hooks
  if (!isValidPost || !postData) {
    return null;
  }
  const postUriParts = postData.uri.split('/');
  const postId = postUriParts[postUriParts.length - 1];
  
  const handleProfileClick = (e) => {
	e.preventDefault();
	setShowProfile(true);
  };

  const handleBookmarkClick = () => {
	if (isSavedView) {
	  if (onBookmarkRemove) {
		onBookmarkRemove(post.post.cid);
	  }
	  return;
	}
	
	const isCurrentlyBookmarked = bookmarkService.isBookmarked(post.post.cid);
	if (isCurrentlyBookmarked) {
	  bookmarkService.removeBookmark(post.post.cid);
	} else {
	  bookmarkService.addBookmark(post);
	}
	setIsBookmarked(!isCurrentlyBookmarked);
  };

  const handleLikeClick = () => {
	if (!isLoggedIn) {
	  onLoginRequired();
	  return;
	}
	onLike(post.post.uri, post.post.cid, post.post.viewer?.like);
  };

  const handleShare = async () => {
    // Construct the GovSky URL for the post
    const govSkyUrl = `${window.location.origin}/post/${postData.author.handle}/${postId}`;
  
    // Check if Web Share API is available (iOS/Safari)
	if (navigator.share) {
	  try {
		await navigator.share({
		  title: `Post by ${postData.author.displayName || postData.author.handle} on GovSky`,
		  text: postData.record.text,
		  url: govSkyUrl
		});
	  } catch (err) {
		if (err.name !== 'AbortError') {
		  handleFallbackCopy(govSkyUrl);
		}
	  }
	} else {
	  handleFallbackCopy(govSkyUrl);
	}
  };
  
  const handleFallbackCopy = async (url) => {
	try {
	  await navigator.clipboard.writeText(url);
      toast.success('Link copied!', {
        duration: 2000,
        position: 'bottom-center',
        style: {
          background: 'var(--background-primary)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          padding: '12px 20px',
          fontSize: '14px'
        }
      });
    } catch (err) {
      toast.error('Failed to copy link', {
        duration: 2000,
        position: 'bottom-center'
      });
    }
  };

  const handleReplyClick = () => {
    if (!isLoggedIn) {
      onLoginRequired();
      return;
    }
    setShowReplies(!showReplies);
  };

  const handleRepostClick = () => {
    if (!isLoggedIn) {
      onLoginRequired();
      return;
    }
    if (onRepost) {
      onRepost(postData.uri, postData.cid, postData.viewer?.repost);
    }
  };

  // Get the proper Bluesky CDN image URL from embed data
  const getImageUrl = (image, size = 'feed_thumbnail') => {
    // If it's already a full URL (from embed.images array in API response)
    if (image?.thumb) return image.thumb;
    if (image?.fullsize) return image.fullsize;
    
    // Handle blob references for images in record.embed
    if (image?.image?.ref?.$link) {
      const mimeType = image.image.mimeType || 'jpeg';
      return `https://cdn.bsky.app/img/${size}/plain/${postData.author.did}/${image.image.ref.$link}@${mimeType}`;
    }
    
    return null;
  };

  // Use embed from the post object (already resolved) or fall back to record.embed
  const embed = postData.embed || postData.record?.embed;

  return (
    <div className="post">
      <div className="post-header">
        <div className="post-author-info" onClick={handleProfileClick}>
          {/* Author Avatar */}
          <img 
            src={postData.author.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(postData.author.displayName || postData.author.handle)}&background=0066cc&color=fff`}
            alt={`${postData.author.displayName || postData.author.handle}'s avatar`}
            className="author-avatar"
            loading="lazy"
          />
          <div className="author-details">
            <h3 className="author-name">
              {postData.author.displayName || formatHandle(postData.author.handle)}
            </h3>
            <span className="author-handle">@{postData.author.handle}</span>
          </div>
        </div>
        <Link 
          to={`/post/${postData.author.handle}/${postId}`}
          className="post-time"
        >
          {formatTimestamp(postData.record.createdAt)}
        </Link>
      </div>

      <div className="post-content-wrapper">
        {/* Post Text with Profanity Filter */}
        <CensoredContent text={postData.record.text} />

        {/* External Link Embed */}
        {embed?.$type === 'app.bsky.embed.external#view' && (
          <LinkPreview embed={embed.external} />
        )}
        {embed?.$type === 'app.bsky.embed.external' && (
          <LinkPreview embed={embed.external} />
        )}
        
        {/* Images Embed */}
        {(embed?.$type === 'app.bsky.embed.images#view' || embed?.$type === 'app.bsky.embed.images') && embed.images && (
          <div className={`post-images grid-${Math.min(embed.images.length, 4)}`}>
            {embed.images.slice(0, 4).map((img, index) => {
              const thumbnailUrl = img.thumb || getImageUrl(img, 'feed_thumbnail');
              const fullUrl = img.fullsize || getImageUrl(img, 'feed_fullsize');

              if (!thumbnailUrl) return null;

              return (
                <div 
                  key={index} 
                  className="post-image-container"
                  onClick={() => fullUrl && window.open(fullUrl, '_blank')}
                  style={{
                    aspectRatio: img.aspectRatio
                      ? `${img.aspectRatio.width}/${img.aspectRatio.height}`
                      : '16/9'
                  }}
                >
                  <img 
                    src={thumbnailUrl}
                    alt={img.alt || 'Post image'} 
                    loading="lazy"
                    className="post-image"
                  />
                  {img.alt && <span className="image-alt">{img.alt}</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Quote Post Embed */}
        {(embed?.$type === 'app.bsky.embed.record#view' || embed?.$type === 'app.bsky.embed.record') && embed.record && (
          <div className="quoted-post">
            <div className="quoted-author">
              <span className="quoted-author-name">
                {embed.record.author?.displayName || embed.record.author?.handle}
              </span>
              <span className="quoted-author-handle">
                @{embed.record.author?.handle}
              </span>
            </div>
            <p className="quoted-post-content">
              {embed.record.value?.text || embed.record.text}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="post-actions">
          <button
            className={`action-button reply-button ${showReplies ? 'active' : ''}`}
            onClick={handleReplyClick}
            aria-label={`${postData.replyCount || 0} replies`}
            title="Reply"
          >
            <MessageCircle size={18} />
            <span className="action-count">{postData.replyCount || 0}</span>
          </button>

          <button
            className={`action-button repost-button ${postData.viewer?.repost ? 'reposted' : ''}`}
            onClick={handleRepostClick}
            disabled={isLoading}
            aria-label={`${postData.repostCount || 0} reposts`}
            title="Repost"
          >
            <Repeat2 size={18} />
            <span className="action-count">{postData.repostCount || 0}</span>
          </button>

          <button
            className={`action-button like-button ${postData.viewer?.like ? 'liked' : ''}`}
            onClick={handleLikeClick}
            disabled={isLoading}
            aria-label={`${postData.likeCount || 0} likes`}
            title="Like"
          >
            <Heart size={18} fill={postData.viewer?.like ? 'currentColor' : 'none'} />
            <span className="action-count">{postData.likeCount || 0}</span>
          </button>

          <button
            className={`action-button bookmark-button ${isSavedView || isBookmarked ? 'bookmarked' : ''}`}
            onClick={handleBookmarkClick}
            aria-label={isSavedView || isBookmarked ? 'Remove from saved' : 'Save post'}
            title={isSavedView || isBookmarked ? 'Remove bookmark' : 'Bookmark'}
          >
            <Bookmark size={18} fill={isSavedView || isBookmarked ? 'currentColor' : 'none'} />
          </button>

          <button
            className="action-button share-button"
            onClick={handleShare}
            aria-label="Share post"
            title="Share"
          >
            <Share2 size={18} />
          </button>
        </div>

		{/* Replies Section */}
		{showReplies && (
		  <ReplySection
			postUri={postData.uri}
			postCid={postData.cid}
			bluesky={bluesky}
			isLoggedIn={isLoggedIn}
			onLoginRequired={onLoginRequired}
			bookmarkService={bookmarkService}
			onLike={onLike}
		  />
		)}
		
		{showProfile && (
		  <UserProfile
			actor={postData.author}
			onClose={() => setShowProfile(false)}
			bluesky={bluesky}
			isLoggedIn={isLoggedIn}
			onLoginRequired={onLoginRequired}
			isModal={true}
		  />
		)}
	  </div>
	</div>
  );
};

export default Post;